import { isDefined } from '@sgme/fp';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

import { useSgPickerInputContext } from './context';

type InputProps = {
  'data-e2e'?: string;
};

export function Input(props: InputProps) {
  const { 'data-e2e': dataE2E } = props;

  const intl = useIntl();

  const {
    canCreateCustomItem,
    inputValue,
    createCustomItemHandler,
    inputId = '',
    labelledBy,
    getInputProps,
    placeholder,
    disabled,
    readonly,
    onPaste,
    onBlurInput,
    onFocus,
    onBackspaceRemove,
    highlightedIndex,
    inputRef,
    selectedItem,
    selectedItems,
    itemSelection,
    onClear,
    setFocused,
  } = useSgPickerInputContext();

  // Use translation key based on itemSelection if no placeholder is provided
  const resolvedPlaceholder = placeholder
    ? placeholder
    : intl.formatMessage({
        id: itemSelection === 'single' ? 'sgds.picker.placeholder.single' : 'sgds.picker.placeholder.multiple',
      });

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent any action if disabled or readonly
    if (disabled || readonly) {
      return;
    }

    if (event.key === 'Enter' && canCreateCustomItem) {
      // Don't trigger create item if an item is highlighted
      // That means the user is selecting an existing item
      if (typeof highlightedIndex === 'number' && highlightedIndex >= 0) {
        return;
      }

      event.preventDefault();
      createCustomItemHandler(inputValue);
    }

    // Handle backspace behavior
    if (event.key === 'Backspace') {
      if (itemSelection === 'single') {
        // In single mode: if an item is selected, clear all text and deselect
        if (selectedItem !== null) {
          event.preventDefault();
          onClear?.(); // Clear the input text
          onBackspaceRemove?.(); // Deselect the item
        }
        // If nothing is selected, allow default behavior (delete one character)
      } else {
        // Handle backspace to remove last selected item in multiple mode
        if (inputValue === '' && onBackspaceRemove) {
          event.preventDefault();
          onBackspaceRemove();
        }
      }
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (onPaste) {
      event.preventDefault();
      const pastedText = event.clipboardData.getData('text');
      onPaste(pastedText);
    }
  };

  const handleBlur = () => {
    // Update focus state
    setFocused?.(false);

    // In single selection mode, don't clear the input on blur
    // The parent component will handle displaying the selected item's label
    if (itemSelection === 'single') {
      // Just call the original onBlur handler
      onBlurInput?.();
      return;
    }

    // For multiple selection mode, keep the original logic
    const hasSelection = selectedItems && selectedItems.length > 0;

    // If there's text in the input but no selection was made, clear the input
    if (inputValue.trim() !== '' && !hasSelection) {
      onClear?.();
    }

    // Call the original onBlur handler if provided
    onBlurInput?.();
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // Update focus state
    setFocused?.(true);

    // Call the original onFocus handler if provided
    onFocus?.(event);
  };

  return (
    <input
      data-e2e={isDefined(dataE2E) ? `${dataE2E}-input` : undefined}
      {...getInputProps?.({
        id: inputId,
        ref: inputRef,
        placeholder: resolvedPlaceholder,
        disabled: disabled,
        readOnly: readonly,
        className: clsx('text-primary py-0 bg-transparent border-0 flex-grow-1'),
        onKeyDown: handleKeyDown,
        onPaste: handlePaste,
        onBlur: handleBlur,
        onFocus: handleFocus,
        ...(labelledBy ? { 'aria-labelledby': labelledBy } : {}),
      })}
      // mt-1 => marginTop: .25rem and not 2px
      style={{ marginTop: '2px', outline: 'none', cursor: readonly ? 'default' : 'text' }}
      size={1}
      autoComplete="off"
    />
  );
}
